import React, { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, BookOpen, Menu, Plus, Send, ShieldCheck, Sparkles, Trash2, X } from 'lucide-react';
import { Link } from 'react-router';

type PublicCitation = {
  citationId: string;
  title: string;
  authority: string;
  section?: string;
  page?: number;
  excerpt?: string;
  edition?: string;
  effectiveAt?: string;
  evidenceClass: string;
};

type ChatMessage = {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  agentName?: string;
  citations?: PublicCitation[];
  limitations?: string[];
  withheld?: boolean;
};

type StreamPayload = {
  displayName?: string;
  agent?: { displayName?: string };
  content?: string;
  outcome?: 'answer' | 'withheld';
  citations?: PublicCitation[];
  limitations?: string[];
  message?: string;
};

const STORAGE_KEY = 'aviagov-public-flymate-session-v1';
const STARTER_PROMPTS = [
  'What does ICAO Annex 15 require for AIP publication?',
  'Explain the operational purpose of a NOTAM.',
  'How do AIRAC and eAIP publication cycles relate?',
  'Give me a grounded overview of PANS-OPS.',
];

const INITIAL_MESSAGE: ChatMessage = {
  id: 'flymate-welcome',
  role: 'assistant',
  agentName: 'FlyMate Manager',
  content: 'Hello — I’m FlyMate. Ask me an aviation question and I’ll route it to the right specialist, using only eligible public knowledge-base evidence.',
};

const messageId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `flymate-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

function parseEvent(block: string) {
  const lines = block.split(/\r?\n/);
  const event = lines.find((line) => line.startsWith('event:'))?.slice(6).trim() || 'message';
  const data = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trim()).join('\n');
  if (!data) return null;
  try {
    return { event, payload: JSON.parse(data) as StreamPayload };
  } catch {
    return null;
  }
}

const PublicFlyMateShell: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [status, setStatus] = useState('Ready');
  const [isSending, setIsSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [restored, setRestored] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const closeNavigationRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length) setMessages(parsed.slice(-20));
      }
    } catch {
      sessionStorage.removeItem(STORAGE_KEY);
    } finally {
      setRestored(true);
    }
  }, []);

  useEffect(() => {
    if (!restored) return;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-20)));
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, restored]);

  useEffect(() => {
    if (!sidebarOpen) return;
    closeNavigationRef.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSidebarOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [sidebarOpen]);

  const recentQuestions = useMemo(
    () => messages.filter((message) => message.role === 'user').slice(-5).reverse(),
    [messages],
  );

  const clearSession = () => {
    setMessages([INITIAL_MESSAGE]);
    setInput('');
    setStatus('Ready');
    sessionStorage.removeItem(STORAGE_KEY);
    setSidebarOpen(false);
  };

  const sendMessage = async (messageText: string) => {
    const text = messageText.trim();
    if (!text || isSending) return;
    const history = messages
      .filter((message) => message.id !== INITIAL_MESSAGE.id)
      .slice(-6)
      .map(({ role, content }) => ({ role, content: content.slice(0, 1_600) }));
    const userMessage: ChatMessage = { id: messageId(), role: 'user', content: text };
    setMessages((current) => [...current, userMessage]);
    setInput('');
    setIsSending(true);
    setStatus('FlyMate Manager is reviewing your request…');

    let completed = false;
    try {
      const response = await fetch('/api/flymate/platform/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          requestId: messageId(),
          message: text,
          conversation: history,
          locale: document.documentElement.lang || 'en',
        }),
      });
      if (!response.ok || !response.body) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'FlyMate is temporarily unavailable.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
        const blocks = buffer.split(/\r?\n\r?\n/);
        buffer = blocks.pop() || '';
        for (const block of blocks) {
          const parsed = parseEvent(block);
          if (!parsed) continue;
          if (parsed.event === 'handoff') {
            setStatus(`${parsed.payload.displayName || 'A FlyMate specialist'} is reviewing the evidence…`);
          }
          if (parsed.event === 'error') throw new Error(parsed.payload.message || 'FlyMate could not complete the request.');
          if (parsed.event === 'final_outcome') {
            completed = true;
            setMessages((current) => [...current, {
              id: messageId(),
              role: 'assistant',
              agentName: parsed.payload.agent?.displayName || 'FlyMate Manager',
              content: parsed.payload.content || 'No answer was returned.',
              citations: parsed.payload.citations || [],
              limitations: parsed.payload.limitations || [],
              withheld: parsed.payload.outcome === 'withheld',
            }]);
          }
        }
        if (done) break;
      }
      if (!completed) throw new Error('FlyMate ended the response before an answer was available.');
      setStatus('Ready');
    } catch (error) {
      setMessages((current) => [...current, {
        id: messageId(),
        role: 'assistant',
        agentName: 'FlyMate Manager',
        withheld: true,
        content: error instanceof Error ? error.message : 'FlyMate is temporarily unavailable.',
      }]);
      setStatus('Request could not be completed');
    } finally {
      setIsSending(false);
    }
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void sendMessage(input);
  };

  const sidebar = (
    <aside className="flex h-full w-[292px] flex-col border-r border-white/10 bg-slate-950/95 p-4 backdrop-blur-xl">
      <div className="flex items-center justify-between gap-3 px-2 py-2">
        <Link to="/" className="flex items-center gap-3 text-white" aria-label="AviaGov home">
          <img src="/AviaGov.svg" alt="AviaGov" className="h-8 w-auto" />
          <span className="rounded-full border border-cyan-400/25 bg-cyan-400/10 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-300">Public</span>
        </Link>
        <button ref={closeNavigationRef} className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-white lg:hidden" onClick={() => setSidebarOpen(false)} aria-label="Close navigation">
          <X className="h-5 w-5" />
        </button>
      </div>

      <button onClick={clearSession} className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-semibold text-white transition hover:border-cyan-400/40 hover:bg-cyan-400/10">
        <Plus className="h-4 w-4" /> New grounded chat
      </button>

      <div className="mt-7 min-h-0 flex-1 overflow-y-auto">
        <p className="px-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">This session</p>
        <div className="mt-2 space-y-1">
          {recentQuestions.length ? recentQuestions.map((question) => (
            <button key={question.id} onClick={() => void sendMessage(question.content)} className="w-full truncate rounded-lg px-2.5 py-2 text-left text-sm text-slate-400 hover:bg-white/5 hover:text-white">
              {question.content}
            </button>
          )) : <p className="px-2 py-3 text-sm leading-6 text-slate-600">Your recent questions appear here only for this browser session.</p>}
        </div>
      </div>

      <div className="space-y-3 border-t border-white/10 pt-4">
        <div className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.07] p-3">
          <div className="flex items-center gap-2 text-xs font-bold text-emerald-300"><ShieldCheck className="h-4 w-4" /> Public grounded mode</div>
          <p className="mt-2 text-xs leading-5 text-slate-400">No account, saved history, uploads, tenant records, or operational actions.</p>
        </div>
        <button onClick={clearSession} className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-sm text-slate-500 hover:bg-white/5 hover:text-white">
          <Trash2 className="h-4 w-4" /> Clear session
        </button>
      </div>
    </aside>
  );

  return (
    <section className="relative h-[100svh] min-h-[620px] overflow-hidden border-y border-white/10 bg-[#050b18] text-white lg:h-[calc(100vh-5rem)]" aria-label="FlyMate public aviation assistant">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_0%,rgba(6,182,212,0.1),transparent_38%)]" />
      <div className="relative flex h-full min-h-0">
        <div className="hidden lg:block">{sidebar}</div>
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 flex lg:hidden" role="dialog" aria-modal="true" aria-label="FlyMate navigation">
            <button className="absolute inset-0 bg-black/70" onClick={() => setSidebarOpen(false)} aria-label="Close navigation overlay" />
            <div className="relative h-full">{sidebar}</div>
          </div>
        )}

        <main className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-16 shrink-0 items-center justify-between border-b border-white/10 bg-slate-950/60 px-4 backdrop-blur-xl sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <button onClick={() => setSidebarOpen(true)} className="rounded-lg p-2 text-slate-300 hover:bg-white/5 lg:hidden" aria-label="Open navigation"><Menu className="h-5 w-5" /></button>
              <div className="min-w-0">
                <h2 className="truncate text-sm font-bold sm:text-base">FlyMate Aviation Assistant</h2>
                <p className="truncate text-xs text-slate-500">Manager + public specialist knowledge</p>
              </div>
            </div>
            <Link to="/contact" className="hidden items-center gap-2 rounded-full border border-cyan-400/25 px-3 py-1.5 text-xs font-bold text-cyan-300 hover:bg-cyan-400/10 sm:flex">Enterprise access <ArrowRight className="h-3.5 w-3.5" /></Link>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-7 sm:px-6 lg:px-10">
            <div className="mx-auto max-w-4xl space-y-6">
              {messages.map((message) => (
                <article key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[92%] sm:max-w-[82%] ${message.role === 'user' ? 'rounded-2xl rounded-br-md bg-cyan-500 px-4 py-3 text-slate-950' : ''}`}>
                    {message.role === 'assistant' && (
                      <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-cyan-300">
                        <span className="grid h-7 w-7 place-items-center rounded-lg bg-cyan-400/10"><Sparkles className="h-4 w-4" /></span>
                        {message.agentName}
                      </div>
                    )}
                    <p className={`whitespace-pre-wrap text-sm leading-7 sm:text-[15px] ${message.role === 'assistant' ? (message.withheld ? 'rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-4 text-slate-200' : 'text-slate-200') : 'font-medium'}`}>{message.content}</p>
                    {!!message.citations?.length && (
                      <div className="mt-4 grid gap-2">
                        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500"><BookOpen className="h-3.5 w-3.5" /> Sources</p>
                        {message.citations.map((citation) => (
                          <details key={citation.citationId} className="rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm">
                            <summary className="cursor-pointer font-semibold text-slate-200">{citation.authority} · {citation.title}{citation.section ? ` · ${citation.section}` : ''}</summary>
                            {citation.excerpt && <p className="mt-2 border-l-2 border-cyan-400/30 pl-3 text-xs leading-5 text-slate-400">{citation.excerpt}</p>}
                          </details>
                        ))}
                      </div>
                    )}
                    {!!message.limitations?.length && <p className="mt-3 text-xs leading-5 text-slate-500">{message.limitations.join(' ')}</p>}
                  </div>
                </article>
              ))}

              {messages.length === 1 && (
                <div className="grid gap-3 pt-2 sm:grid-cols-2">
                  {STARTER_PROMPTS.map((prompt) => (
                    <button key={prompt} onClick={() => void sendMessage(prompt)} className="group rounded-2xl border border-white/10 bg-white/[0.025] p-4 text-left text-sm leading-6 text-slate-400 transition hover:border-cyan-400/35 hover:bg-cyan-400/[0.06] hover:text-white">
                      {prompt}<ArrowRight className="mt-3 h-4 w-4 text-slate-600 transition group-hover:translate-x-1 group-hover:text-cyan-300" />
                    </button>
                  ))}
                </div>
              )}
              {isSending && <div className="flex items-center gap-3 text-sm text-slate-400"><span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" />{status}</div>}
              <div ref={endRef} />
            </div>
          </div>

          <div className="shrink-0 border-t border-white/10 bg-slate-950/80 px-4 py-4 backdrop-blur-xl sm:px-6">
            <form onSubmit={submit} className="mx-auto max-w-4xl">
              <label htmlFor="flymate-message" className="sr-only">Ask FlyMate an aviation question</label>
              <div className="flex items-end gap-2 rounded-2xl border border-white/10 bg-white/[0.045] p-2 shadow-2xl shadow-black/20 focus-within:border-cyan-400/50">
                <textarea id="flymate-message" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    submit(event as unknown as FormEvent);
                  }
                }} rows={1} maxLength={2000} disabled={isSending} placeholder="Ask about ICAO, AIM, NOTAM, FPL, SMS, training, or procedures…" className="max-h-36 min-h-11 flex-1 resize-none bg-transparent px-3 py-2.5 text-sm leading-6 text-white outline-none placeholder:text-slate-600 disabled:opacity-60" />
                <button type="submit" disabled={isSending || !input.trim()} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-cyan-400 text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Send message"><Send className="h-4 w-4" /></button>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 px-1 text-[11px] text-slate-600">
                <span aria-live="polite">{status}</span>
                <span>Read-only · Verify critical information</span>
              </div>
            </form>
          </div>
        </main>
      </div>
    </section>
  );
};

export default PublicFlyMateShell;
